import { DateFormatter } from 'infrastructure/functions';

import {
  monthlyTemplate_step_1,
  monthlyTemplate_step_2,
  monthlyTemplate_step_3,
  type MonthlySteppedValues,
} from '../templates/monthly';

const isStepEmpty = (step: Record<string, unknown>) => {
  return Object.values(step).every(
    (i) => !i || (Array.isArray(i) && !i.length),
  );
};

export const generateMonthlyBlurb = (data: MonthlySteppedValues) => {
  const { STEP1, STEP2, STEP3, STEP4, STEP5, STEP6, STEP7 } = data;
  let text = '';

  // -- STEP 1 --
  if (!isStepEmpty(STEP1)) {
    if (STEP1.hasRecentHealthcareActivity === 'yes') {
      text += 'Patient had recent healthcare activity including ';
      if (STEP1.recentHealthcareActivity?.length) {
        const labels = monthlyTemplate_step_1[0].options[0].subItems[0].options
          .filter(
            (i) =>
              STEP1.recentHealthcareActivity?.includes(i.value) &&
              i.value !== 'other',
          )
          .map((i) => i.label.toString());
        if (
          STEP1.recentHealthcareActivity?.includes('other') &&
          STEP1.recentHealthcareActivityOtherText?.trim()
        ) {
          labels.push(STEP1.recentHealthcareActivityOtherText);
        }
        text += `${labels.join(', ')}. `;
      }
    }

    if (STEP1.hasRecentHealthcareActivity === 'no') {
      text += 'Patient had no recent healthcare activity at this time. ';
    }
  }

  if (!isStepEmpty(STEP2)) {
    if (STEP2.wasMedRecPerformed !== undefined) {
      text += '\n';
      text += 'MedRec ';
      if (STEP2.wasMedRecPerformed === 'yes') text += 'was ';
      if (STEP2.wasMedRecPerformed === 'no') text += 'was not ';
      text += 'performed. ';

      if (STEP2.timing?.length && STEP2.wasMedRecPerformed === 'yes') {
        const labels = monthlyTemplate_step_2[0].options[0].subItems[0].options
          .filter((i) => STEP2.timing?.includes(i.value))
          .map((i) => i.label);
        text += `MedRec was performed during ${labels.join(', ')}. `;
      }
    }

    if (STEP2.medicationDiscrepancies !== undefined) {
      text += '\n';
      text += 'Medication discrepancies ';
      if (STEP2.medicationDiscrepancies === 'yes') text += 'were ';
      if (STEP2.medicationDiscrepancies === 'no') text += 'were not ';
      text += 'identified. ';

      if (STEP2.medicationDiscrepanciesYesText?.trim()) {
        text += `${STEP2.medicationDiscrepanciesYesText}. `;
      }
    }

    if (STEP2.patientAdherent !== undefined) {
      text += '\n';
      text += 'Patient ';
      if (STEP2.patientAdherent === 'yes') text += 'is ';
      if (STEP2.patientAdherent === 'no') text += 'is not ';
      text += 'adherent. ';

      if (STEP2.patientAdherentNoText?.trim()) {
        text += `Reason: ${STEP2.patientAdherentNoText}. `;
      }
    }

    if (STEP2.adherenceBarriersDiscussed !== undefined) {
      text += '\n';
      text += 'Adherence/barriers ';
      if (STEP2.adherenceBarriersDiscussed === 'yes') text += 'were ';
      if (STEP2.adherenceBarriersDiscussed === 'no') text += 'were not ';
      text += 'discussed. ';

      if (STEP2.adherenceBarriersIndicated === 'yes') {
        text += `Adherence/barriers indicated: ${STEP2.adherenceBarriersIndicatedYesText}. `;
      }
      if (STEP2.adherenceBarriersIndicated === 'no') {
        const labels =
          monthlyTemplate_step_2[3].options[0].subItems[0].options[1];
        text += `Adherence/barriers indicated: ${labels.label}. `;
      }
    }

    if (STEP2.changesSideEffectsNoted !== undefined) {
      text += '\n';
      text += 'Changes/side-effects ';
      if (STEP2.changesSideEffectsNoted === 'yes') text += 'were ';
      if (STEP2.changesSideEffectsNoted === 'no') text += 'were not ';
      text += 'noted. ';

      if (STEP2.changesSideEffectsNotedYesText?.trim()) {
        text += `Details: ${STEP2.changesSideEffectsNotedYesText}. `;
      }
    }
  }

  if (!isStepEmpty(STEP3)) {
    if (STEP3.didNurseUpdateCarePlan?.length) {
      text += '\n';
      text += 'Care plan updates: ';
      const labels = monthlyTemplate_step_3[0].options
        .filter((i) => STEP3.didNurseUpdateCarePlan?.includes(i.value))
        .map((i) => i.label);
      text += `${labels.join(', ')}. `;
    }

    if (STEP3.goals?.length) {
      text += '\n';
      text += 'Goals: ';
      let goalsLabels: string[] = [];
      const betterControlOfLabel = monthlyTemplate_step_3[1].options[0].label;

      const labels = monthlyTemplate_step_3[1].options
        .filter((i) => STEP3.goals?.includes(i.value))
        .map((i) => i.label);

      goalsLabels = labels;

      if (goalsLabels.includes(betterControlOfLabel)) {
        const betterControlOfLabels =
          monthlyTemplate_step_3[1].options[0].subItems[0].options
            .filter((i) => STEP3.betterControlOfList?.includes(i.value))
            .map((i) => i.label);
        const labelsWithBetterControlOf = goalsLabels.map((i) =>
          i.replace(
            betterControlOfLabel,
            `Better control of: ${betterControlOfLabels.join(', ')}`,
          ),
        );
        goalsLabels = labelsWithBetterControlOf;
      }
      if (goalsLabels.includes('other') && STEP3.goalsOtherText?.trim()) {
        const textWithOther = goalsLabels.map((i) =>
          i.replace('other', STEP3.goalsOtherText?.trim()!),
        );
        goalsLabels = textWithOther;
      }

      text += `${goalsLabels.join(', ')}. `;
    }

    if (STEP3.measurableTargets !== undefined) {
      text += '\n';
      text += 'Measurable targets: ';
      const label = monthlyTemplate_step_3[2].options.find(
        (i) => STEP3.measurableTargets === i.value,
      )?.label;

      if (label !== 'other') text += `${label}. `;

      if (label === 'other' && STEP3.measurableTargetsOtherText?.trim()) {
        if (label) text += `${STEP3.measurableTargetsOtherText?.trim()}. `;
      }
    }

    if (STEP3.barriers?.length) {
      text += '\n';
      text += 'Barriers: ';
      const labels = monthlyTemplate_step_3[3].options
        .filter((i) => STEP3.barriers?.includes(i.value))
        .map((i) => i.label);

      if (labels.includes('other') && STEP3.barriersOtherText?.trim()) {
        text += `${labels
          .map((i) => i.replace('other', STEP3.barriersOtherText?.trim()!))
          .join(', ')}. `;
      } else text += `${labels.join(', ')}. `;
    }

    if (STEP3.dateCarePlanLastUpdated !== undefined) {
      text += '\n';
      text += `Care plan last updated on ${DateFormatter({
        date: STEP3.dateCarePlanLastUpdated,
      })}. `;
    }

    if (STEP3.carePlanReviewedAtThisEncounter !== undefined) {
      text += '\n';
      text += 'Care plan ';
      if (STEP3.carePlanReviewedAtThisEncounter === 'yes') text += 'was ';
      if (STEP3.carePlanReviewedAtThisEncounter === 'no') text += 'was not ';
      text += 'reviewed at this encounter. ';
    }
  }

  if (!isStepEmpty(STEP4)) {
    if (STEP4.adlFunctionalStatusChecked !== undefined) {
      text += '\n';
      text += 'ADL/functional status ';
      if (STEP4.adlFunctionalStatusChecked === 'yes') text += 'was ';
      if (STEP4.adlFunctionalStatusChecked === 'no') text += 'was not ';
      text += 'checked. ';
    }

    if (STEP4.cognitiveStatusScreened !== undefined) {
      text += '\n';
      text += 'Cognitive status screening ';
      if (STEP4.cognitiveStatusScreened === 'yes') text += 'took place. ';
      if (STEP4.cognitiveStatusScreened === 'no')
        text += 'did not take place. ';
      if (STEP4.cognitiveStatusScreened === 'notIndicated')
        text += 'not indicated. ';
    }

    if (STEP4.behavioralHealthScreening !== undefined) {
      text += '\n';
      text += 'Behavioral health screening (PHQ-2) ';
      if (STEP4.behavioralHealthScreening === 'yes') text += 'took place. ';
      if (STEP4.behavioralHealthScreening === 'no')
        text += 'did not take place. ';
    }

    if (STEP4.fallSafetyRiskAssessed !== undefined) {
      text += '\n';
      text += 'Fall/safety risk assessed ';
      if (STEP4.fallSafetyRiskAssessed === 'yes') text += 'was ';
      if (STEP4.fallSafetyRiskAssessed === 'no') text += 'was not ';
      text += 'assessed. ';
    }

    if (STEP4.escalationsRedFlagsToday !== undefined) {
      text += '\n';
      text += 'Escalations/red flags ';
      if (STEP4.escalationsRedFlagsToday === 'yes') text += 'indicated. ';
      if (STEP4.escalationsRedFlagsToday === 'no')
        text += 'were not indicated. ';
      if (STEP4.escalationsRedFlagsTodayYesText?.trim()) {
        text += `${STEP4.escalationsRedFlagsTodayYesText?.trim()}. `;
      }
    }
  }

  if (!isStepEmpty(STEP5)) {
    if (STEP5.sdohNeedsAssessmentUpdated !== undefined) {
      text += '\n';
      text += 'SDOH/needs assessment ';
      if (STEP5.sdohNeedsAssessmentUpdated === 'yes') text += 'was ';
      if (STEP5.sdohNeedsAssessmentUpdated === 'no') text += 'was not ';
      text += 'updated. ';
    }

    if (STEP5.anyUnmetNeedsOrReferralsThisMonth !== undefined) {
      text += '\n';
      text += 'Unmet needs or referrals ';
      if (STEP5.anyUnmetNeedsOrReferralsThisMonth === 'yes') text += 'were ';
      if (STEP5.anyUnmetNeedsOrReferralsThisMonth === 'no') text += 'were not ';
      text += 'indicated. ';
      if (STEP5.anyUnmetNeedsOrReferralsThisMonthYesText?.trim()) {
        text += `${STEP5.anyUnmetNeedsOrReferralsThisMonthYesText?.trim()}. `;
      }
    }
  }

  if (!isStepEmpty(STEP6)) {
    if (STEP6.preventiveCareReviewed !== undefined) {
      text += '\n';
      text += 'Preventative care ';
      if (STEP6.preventiveCareReviewed === 'yes') text += 'was ';
      if (STEP6.preventiveCareReviewed === 'no') text += 'was not ';
      text += 'reviewed. ';
    }

    if (STEP6.outstandingItemsNextDue?.trim()) {
      text += '\n';
      text += 'Outstanding items/next due: ';
      text += `${STEP6.outstandingItemsNextDue?.trim()}. `;
    }
  }

  if (!isStepEmpty(STEP7)) {
    if (STEP7.coordinationWithProviders !== undefined) {
      text += '\n';
      text +=
        'Coordination with providers/pharmacies/community agencies  as needed ';
      if (STEP7.coordinationWithProviders === 'yes') text += 'was ';
      if (STEP7.coordinationWithProviders === 'no') text += 'was not ';
      text += 'indicated. ';
    }

    if (STEP7.anyReferralsFollowUps !== undefined) {
      text += '\n';
      text += 'Referrals/follow-ups ';
      if (STEP7.anyReferralsFollowUps === 'yes') text += 'were ';
      if (STEP7.anyReferralsFollowUps === 'no') text += 'were not ';
      text += 'documented this month. ';
      if (STEP7.anyReferralsFollowUpsYesText?.trim()) {
        text += `${STEP7.anyReferralsFollowUpsYesText?.trim()}. `;
      }
    }
  }

  return text;
};
